'use client';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCalendar, faClock, faFlag } from '@fortawesome/pro-regular-svg-icons';
import { faTicket } from '@fortawesome/pro-solid-svg-icons';
import { faCircleDollar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import { Tooltip } from '@pickleballinc/react-ui';
import { formatCurrency } from '@pickleballinc/ui/components/global/utils/formatCurrency';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import moment from 'moment-timezone';

import { Session } from '@/lib/getSessionPages';
import { useRegistrationStatus } from '@/modules/league/hooks/useRegistrationStatus';
import { LeagueDetails, SessionLeagueDetails } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';

interface RegistrationInfoBoxProps {
  details: LeagueDetails;
  session: SessionLeagueDetails;
  isRegistered: boolean;
  params: { slug: string; sessionId: string };
  user?: Session;
}

export default function RegistrationInfoBox({
  details,
  session,
  isRegistered,
  params,
  user,
}: RegistrationInfoBoxProps) {
  const { slug } = params;
  const { setLoading } = useLoader();

  const isUserLoggedIn = user?.token && user?.pbUuid ? true : false;

  const userTimeInSpecifiedZone = moment().tz(session.Timezone_IanaID).utc();
  const registrationCloseDate = moment.parseZone(session.Registration_DateClosed_UTC);
  const isRegistrationDateAfterNow = userTimeInSpecifiedZone.isAfter(registrationCloseDate);
  const isRegistrationLocked = details.IsLocked;
  const isRegistrationClosed = !isRegistrationLocked && isRegistrationDateAfterNow;
  const isRegistrationOnlyForMembers = !session.AllowNonMemberSignUp;

  const registrationButtonStatus = useRegistrationStatus(
    isRegistrationLocked,
    isRegistrationDateAfterNow,
    isRegistrationOnlyForMembers,
    session,
    isRegistered,
    isUserLoggedIn
  );

  const formatDate = (date: Date) => moment.utc(date).format('MMM DD, YYYY');
  const formatTime = (date: Date) => moment.utc(date).format('h:mm A');

  const dateOfFirstMatch = formatDate(session?.MatchDateFirstDate);
  const dateOfLastMatch = formatDate(session?.MatchDateLastDate);

  const timeOfFirstMatch = formatTime(session?.MatchDateFirstDate);

  const registrationOpenDateAndTime = moment
    .parseZone(session.Registration_DateOpen)
    .format('MMM DD, YYYY h:mm A');
  const registrationCloseDateAndTime = moment
    .parseZone(session.Registration_DateClosed)
    .format('MMM DD, YYYY h:mm A');

  const clubURL = `${process.env.NEXT_PUBLIC_PC_URL}/clubs/${details?.ClubSlug ? details.ClubSlug : details?.ClubID}`;
  const clubInfo = `${details?.ClubTypeTitle}: ${details?.ClubTitle}`;

  const showCurrentAndNextSessionButtons =
    details?.SessionNumberList &&
    details.SessionNumberList > 1 &&
    details.LeagueSessionID_Next !== details.LeagueSessionID_Current;

  const renderTooltipSection = (
    icon: IconProp,
    firstDate: string,
    tooltip: string,
    showInTwoLines?: boolean,
    secondDate?: string
  ) => {
    if (registrationButtonStatus === 'registration-completed') return null;
    return (
      <>
        <Tooltip placement='top-start'>
          <Tooltip.Trigger asChild>
            <div className='flex items-center'>
              <FontAwesomeIcon icon={icon} className='ml-2 mr-4' size='lg' />
              {showInTwoLines && secondDate ? (
                <div className='flex flex-col'>
                  <span>{firstDate}</span>
                  <span>{secondDate}</span>
                </div>
              ) : (
                <span>{firstDate}</span>
              )}
            </div>
          </Tooltip.Trigger>
          <Tooltip.Content className='w-42 bg-opacity/75 rounded bg-black p-2 text-sm text-white'>
            {tooltip}
          </Tooltip.Content>
        </Tooltip>
        <hr className='my-4' />
      </>
    );
  };

  const renderCostSection = () => {
    if (isRegistrationLocked || isRegistrationClosed) return null;

    const {
      Cost_Registration_NonMember: nonMemberCost,
      Cost_Registration_Member: memberCost,
      chargeOptIn,
    } = session;
    const {
      PaymentCurrencyAbbreviation: currencyAbbreviation,
      PaymentCurrencyFormatTag: currencyFormatTag,
    } = details;

    const paymentPerGameDay = chargeOptIn ? 'per game day' : '';
    const registrationCostDiffers = parseInt(nonMemberCost) > 0 && memberCost !== nonMemberCost;

    const cost =
      isRegistrationOnlyForMembers || registrationCostDiffers ? memberCost : nonMemberCost;

    return (
      <>
        <div className='ml-1 flex items-center font-bold'>
          <FontAwesomeIcon icon={faCircleDollar} className='mr-2 text-3xl text-success-600' />
          <div className='flex flex-col'>
            {`Cost ${formatCurrency(cost, currencyAbbreviation, currencyFormatTag)} ${paymentPerGameDay}`}
            {isRegistrationOnlyForMembers ? <p>Club Members ONLY</p> : null}
          </div>
        </div>
        {showCurrentAndNextSessionButtons ? <hr className='my-4' /> : null}
      </>
    );
  };

  const renderSessionsSection = () => {
    if (!showCurrentAndNextSessionButtons) return null;

    const currentSessionLabel = `Current - Session ${details.SessionNumber_Current}`;
    const nextSessionLabel = `Next - Session ${details.SessionNumber_Next}`;

    const currentSessionURL = `${process.env.NEXT_PUBLIC_PL_URL}/leagues/${slug}/session/${details?.LeagueSessionID_Current !== session.LiveSession_LeagueSessionID ? session.LiveSession_LeagueSessionID : details.LeagueSessionID_Current}?tab=standings`;
    const nextSessionURL = `${process.env.NEXT_PUBLIC_PL_URL}/leagues/${slug}/session/${details.LeagueSessionID_Next}?tab=standings`;

    return (
      <div className='flex flex-row justify-between gap-1'>
        <CustomLink href={currentSessionURL}>
          <Button variant='secondary' className='w-full justify-center !text-sm'>
            {currentSessionLabel}
          </Button>
        </CustomLink>
        {details?.LeagueSessionID_Next &&
        details?.LeagueSessionID_Current !== details?.LeagueSessionID_Next ? (
          <CustomLink href={nextSessionURL}>
            <Button
              variant='secondary'
              className='w-full justify-center !text-sm'
              onClick={() => setLoading(true)}
            >
              {nextSessionLabel}
            </Button>
          </CustomLink>
        ) : null}
      </div>
    );
  };

  return (
    <div className='flex flex-col'>
      <div className='flex items-center sm:mt-4 mt-2'>
        <FontAwesomeIcon icon={faFlag} className='ml-2 mr-4' size='lg' />
        <a
          className='text-leagues-300 hover:cursor-pointer hover:text-leagues-300/80'
          href={clubURL}
          aria-label={`Link to ${clubURL}`}
        >
          {clubInfo}
        </a>
      </div>

      <hr className='my-4' />

      {renderTooltipSection(
        faCalendar,
        `${dateOfFirstMatch} - ${dateOfLastMatch}`,
        'Session From and To Dates'
      )}
      {renderTooltipSection(faClock, `Start Time: ${timeOfFirstMatch}`, 'Match Start Time')}
      {renderTooltipSection(
        faTicket,
        `${registrationOpenDateAndTime}`,
        'Registration From and To Dates',
        true,
        `${registrationCloseDateAndTime}`
      )}

      {renderCostSection()}
      {renderSessionsSection()}
    </div>
  );
}
